import { useEffect, useRef, type ReactElement } from 'react'
import { format } from 'date-fns'
import { Download, Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useInvoices, useDeleteInvoice } from '../hooks/useInvoices'
import { downloadInvoiceFile } from '../api/invoicesService'
import type { Invoice } from '../schemas/invoicesSchema'

interface InvoicesTableProps {
  year: number
  monthIndex: number
  onEdit?: (invoice: Invoice) => void
}

const PAGE_SIZE = 50

export function InvoicesTable({ year, monthIndex, onEdit }: InvoicesTableProps): ReactElement {
  const {
    data,
    isLoading,
    isError,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInvoices(year, monthIndex, PAGE_SIZE)

  const deleteMutation = useDeleteInvoice()
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  // Infinite scroll observer
  useEffect(() => {
    const el = loadMoreRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage()
        }
      },
      { root: null, rootMargin: '200px', threshold: 0 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleDownload = async (invoice: Invoice): Promise<void> => {
    try {
      const blob = await downloadInvoiceFile(invoice.uuid)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = invoice.originalFilename ?? `invoice-${invoice.invoiceNumber}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Failed to download file')
    }
  }

  const handleDelete = async (uuid: string): Promise<void> => {
    try {
      await deleteMutation.mutateAsync(uuid)
      toast.success('Invoice deleted')
    } catch {
      toast.error('Failed to delete invoice')
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="py-8 text-center text-destructive">
        Error: {error.message}
      </div>
    )
  }

  const invoices = data?.pages.flatMap(page => page.content) ?? []

  if (invoices.length === 0) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        No invoices for this month
      </div>
    )
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">#</TableHead>
            <TableHead>Invoice No.</TableHead>
            <TableHead>Counterparty</TableHead>
            <TableHead className="w-28">Sale Date</TableHead>
            <TableHead className="w-36 text-right">Amount</TableHead>
            <TableHead className="w-28 text-right" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((inv, idx) => (
            <TableRow key={inv.uuid}>
              <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
              <TableCell className="font-medium truncate">{inv.invoiceNumber}</TableCell>
              <TableCell className="truncate">{inv.counterparty}</TableCell>
              <TableCell>{format(new Date(inv.saleDate), 'dd MMM')}</TableCell>
              <TableCell className="text-right tabular-nums">
                {Number(inv.grossAmount).toLocaleString('en', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                <span className="text-muted-foreground text-xs">{inv.currency}</span>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-0.5">
                  {inv.hasFile && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => void handleDownload(inv)}
                      title="Download file"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => onEdit?.(inv)}
                    title="Edit invoice"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => void handleDelete(inv.uuid)}
                    title="Delete invoice"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div ref={loadMoreRef} className="mt-4 h-8 flex items-center justify-center">
        {isFetchingNextPage && (
          <span className="text-sm text-muted-foreground">Loading more...</span>
        )}
      </div>
    </>
  )
}
